import { useState } from 'react';
import { Button, Callout, DraftDiff, StatusTag, theme } from '@agentisai/design-system';
import type { LibraryAgent, ShareReview } from '@agentisai/shared-types';
import { describeError } from '../lib/describeError';
import { useLibrary, useLibraryAction, useReviews } from '../queries/useLibrary';
import { useMemberName } from '../queries/useMembers';
import { useRole } from '../queries/useRole';

const muted = {
  margin: 0,
  color: theme.color.neutral.faint,
  fontSize: theme.typography.fontSize.bodySm,
  lineHeight: theme.typography.lineHeight.body,
} as const;

const heading = {
  margin: `${theme.space[2]} 0 0`,
  fontSize: theme.typography.fontSize.tiny,
  fontWeight: theme.typography.fontWeight.semibold,
  letterSpacing: theme.typography.letterSpacing.label,
  textTransform: 'uppercase',
  color: theme.color.neutral.muted,
} as const;

const card = {
  display: 'flex',
  flexDirection: 'column',
  gap: theme.space['1_5'],
  padding: theme.space[3],
  borderRadius: theme.radii.lg,
  border: `1px solid ${theme.color.neutral.border}`,
  background: theme.color.neutral.white,
} as const;

const actions = { display: 'flex', flexWrap: 'wrap', gap: theme.space[1] } as const;

/**
 * The org library in the Agents panel (AB-2-5, ADR-0081 §2/§3): shared agents plus your own
 * published ones, each with what you may do to it — share (asks an Owner/Admin), share
 * your latest revision of a shared agent, fork someone else's shared agent into a new
 * builder, retire — and, for an Owner or Admin, the share requests waiting on them.
 * The server enforces every rule; this only hides what you can't do.
 */
export function AgentLibrary({
  onFork,
  forking = false,
}: {
  readonly onFork: (agentId: string) => void;
  /** A fork's builder is being created — every Fork waits, so one click makes one builder. */
  readonly forking?: boolean;
}) {
  const library = useLibrary();
  const role = useRole().data;
  const canReview = role === 'owner' || role === 'admin';
  const reviews = useReviews(canReview);

  let list;
  if (library.isLoading) list = <p style={muted}>Loading the library…</p>;
  else if (library.isError)
    list = (
      <p role="alert" style={muted}>
        Couldn't load the library. We'll retry automatically.
      </p>
    );
  else if ((library.data ?? []).length === 0)
    list = (
      <p style={muted}>
        Nothing shared yet. Publish an agent, then share it with your organisation.
      </p>
    );
  else
    list = (library.data ?? []).map((a) => (
      <LibraryRow
        key={a.agentId}
        agent={a}
        orgOwner={role === 'owner'}
        onFork={onFork}
        forking={forking}
      />
    ));

  return (
    <section aria-label="Organisation library" style={{ display: 'grid', gap: theme.space[2] }}>
      <h3 style={heading}>Library</h3>
      {list}
      {canReview && (reviews.data ?? []).length > 0 && (
        <section aria-label="Waiting for review" style={{ display: 'grid', gap: theme.space[2] }}>
          <h3 style={heading}>Waiting for review</h3>
          {(reviews.data ?? []).map((r) => (
            <ReviewRow key={r.agentId} review={r} />
          ))}
        </section>
      )}
    </section>
  );
}
AgentLibrary.displayName = 'AgentLibrary';

function LibraryRow({
  agent: a,
  orgOwner,
  onFork,
  forking,
}: {
  readonly agent: LibraryAgent;
  readonly orgOwner: boolean;
  readonly onFork: (agentId: string) => void;
  readonly forking: boolean;
}) {
  const action = useLibraryAction(a.agentId);
  // Retire is irreversible for its memberships: one extra click to confirm (local state).
  const [confirming, setConfirming] = useState(false);
  const busy = action.isPending;
  return (
    <article aria-label={a.displayName} style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: theme.space[2] }}>
        <strong style={{ fontSize: theme.typography.fontSize.bodySm }}>{a.displayName}</strong>
        <StatusTag tone={a.status === 'shared' ? 'success' : 'neutral'}>
          {a.status === 'shared' ? 'Shared' : 'Private'} · v{a.version ?? 1}
        </StatusTag>
        {/* Only the owner hears about their pending request; everyone else sees what's live. */}
        {a.mine && a.reviewVersion && (
          <StatusTag tone="info">Waiting for review · v{a.reviewVersion}</StatusTag>
        )}
      </div>
      {a.description && <p style={muted}>{a.description}</p>}
      <div style={actions}>
        {a.mine && a.status === 'private' && !a.reviewVersion && (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => action.mutate({ kind: 'share' })}
          >
            Share with organisation
          </Button>
        )}
        {/* A newer request replaces a pending one, so this stays while one waits. */}
        {a.mine && a.status === 'shared' && (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => action.mutate({ kind: 'share' })}
          >
            Share latest revision
          </Button>
        )}
        {!a.mine && a.status === 'shared' && (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy || forking}
            onClick={() => onFork(a.agentId)}
          >
            Fork
          </Button>
        )}
        {(a.mine || orgOwner) &&
          (confirming ? (
            <>
              <Button
                size="sm"
                variant="destructive"
                disabled={busy}
                onClick={() => action.mutate({ kind: 'retire' })}
              >
                Retire — remove from every workstream
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => setConfirming(false)}
              >
                Cancel
              </Button>
            </>
          ) : (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirming(true)}>
              Retire
            </Button>
          ))}
      </div>
      {action.isError && (
        <div role="alert">
          <Callout tone="danger">{describeError(action.error).userMessage}</Callout>
        </div>
      )}
    </article>
  );
}
LibraryRow.displayName = 'LibraryRow';

function ReviewRow({ review: r }: { readonly review: ShareReview }) {
  const action = useLibraryAction(r.agentId);
  const nameOf = useMemberName();
  const [open, setOpen] = useState(false);
  const update = r.currentVersion !== undefined;
  return (
    <article aria-label={`Review ${r.displayName}`} style={card}>
      <strong style={{ fontSize: theme.typography.fontSize.bodySm }}>
        {r.displayName} · v{r.version}
      </strong>
      <p style={muted}>
        {nameOf(r.requestedBy)} asks to{' '}
        {update
          ? `update the shared agent from v${r.currentVersion}`
          : 'share it with the organisation'}
        .
      </p>
      <p style={muted}>{r.description}</p>
      <Button size="sm" variant="ghost" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {open ? 'Hide the soul' : update ? 'Show what changes' : 'Read the soul'}
      </Button>
      {open && (
        <DraftDiff
          size="sm"
          before={r.currentSoul ?? ''}
          after={r.soul}
          aria-label={
            update ? `Changes from v${r.currentVersion} to v${r.version}` : `Soul of v${r.version}`
          }
        />
      )}
      <div style={actions}>
        <Button
          size="sm"
          variant="secondary"
          disabled={action.isPending}
          onClick={() => action.mutate({ kind: 'review', decision: 'approve' })}
        >
          Approve and share
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={action.isPending}
          onClick={() => action.mutate({ kind: 'review', decision: 'decline' })}
        >
          Decline
        </Button>
      </div>
      {action.isError && (
        <div role="alert">
          <Callout tone="danger">{describeError(action.error).userMessage}</Callout>
        </div>
      )}
    </article>
  );
}
ReviewRow.displayName = 'ReviewRow';
